// Eskom PARCC — Findings filter bar (presentational).
//
// The monolith's findings filter row — Dataset, Compliance, Anchor and a
// free-text search — extended per Task 14 with Category (coarse dataset type)
// and Division selects. Option lists are supplied by the page (enum sets unioned
// with the distinct values actually present), so this component is purely
// controlled: it holds no state and does no derivation.
import { FilterBar, SelectFilter, SearchInput, Button } from '../../components/ui';
import type { FindingFilters as Filters } from './filterFindings';

export function FindingFilters({
  filters,
  onChange,
  onReset,
  datasetOptions,
  categoryOptions,
  divisionOptions,
  complianceOptions,
  anchorOptions,
  reviewedOptions,
}: {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
  datasetOptions: readonly string[];
  categoryOptions: readonly string[];
  divisionOptions: readonly string[];
  complianceOptions: readonly string[];
  anchorOptions: readonly string[];
  reviewedOptions: readonly string[];
}): React.JSX.Element {
  return (
    <div className="filters">
      <FilterBar>
        <SelectFilter
          label="Dataset"
          value={filters.dataset}
          options={withAll(datasetOptions, 'All datasets')}
          onChange={(dataset) => onChange({ dataset })}
        />
        <SelectFilter
          label="Category"
          value={filters.category}
          options={withAll(categoryOptions, 'All categories')}
          onChange={(category) => onChange({ category })}
        />
        <SelectFilter
          label="Division"
          value={filters.division}
          options={withAll(divisionOptions, 'All divisions')}
          onChange={(division) => onChange({ division })}
        />
        <SelectFilter
          label="Compliance"
          value={filters.compliance}
          options={withAll(complianceOptions, 'All compliance levels')}
          onChange={(compliance) => onChange({ compliance })}
        />
        <SelectFilter
          label="Anchor"
          value={filters.anchor}
          options={withAll(anchorOptions, 'All anchors')}
          onChange={(anchor) => onChange({ anchor })}
        />
        <SelectFilter
          label="Reviewed"
          value={filters.reviewed}
          options={withAll(reviewedOptions, 'All review states')}
          onChange={(reviewed) => onChange({ reviewed })}
        />
        <SelectFilter
          label="Disposition"
          value={filters.disposition}
          options={DISPOSITION_OPTIONS}
          onChange={(disposition) => onChange({ disposition })}
        />
        <SearchInput
          label="Search"
          value={filters.search}
          placeholder="Finding, ref, route, rationale, rule…"
          onChange={(search) => onChange({ search })}
        />
      </FilterBar>
      <Button variant="ghost" onClick={onReset}>
        Reset filters
      </Button>
    </div>
  );
}

/** Prepend an "all" sentinel (empty value) to a value list for a select. */
function withAll(values: readonly string[], allLabel: string) {
  return [{ value: '', label: allLabel }, ...values.map((v) => ({ value: v, label: v }))];
}

/**
 * Finding-disposition options (Lindiwe R9). The register defaults to Outstanding —
 * the work still to do — while "All findings" reveals the declared/resolved lines
 * so nothing is hidden, and the two closure routes are each directly filterable.
 */
const DISPOSITION_OPTIONS = [
  { value: '', label: 'All findings' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'self-declared', label: 'Self-declared' },
  { value: 'resolved', label: 'Resolved' },
];
